// @flow
type Pin = {
  board?: {
    url: string,
  },
  id: string,
  pinner?: {
    username: string,
  },
  tracking_params: string,
  tracking_params_map: Object,
};

const getId = (path: string, urlBase: ?string) => {
  if (!urlBase) {
    return path;
  }
  return path.substring(urlBase.length).split('/')[0];
};

/* This function maps the current url to it's resource in order to look up the correct tracking_params field
 * that is being stored in pin.tracking_params_map. These keys are for the map that is created in
 * webapp/app/common/redux/reducers/pins.js after the resource fetch has completed.
 *
 * If you are adding a resource that depends on a specific id (i.e. related pins are associated with
 * a given pin.id), please add that as an indentifier on the map key ('RelatedPinFeedResource_${id}').
 * If you have any questions, please reach out to juliac@.
 */
const getMapKey = (path: string, pin: Pin) => {
  if (!path) {
    return '';
  }

  let urlBase = '';
  // remove additional query strings from path
  const split = path.split('?');
  path = split[0];
  const queryStr = split[1];
  const isLandingPage = /lp=true/.test(queryStr);
  // Board landing pages utilize the search resource
  let boardUrl = !isLandingPage && pin.board && pin.board.url;
  const username = pin.pinner && `/${pin.pinner.username}/`;
  if (boardUrl && username) {
    let boardName = getId(boardUrl, username);
    try {
      boardName = decodeURIComponent(boardName);
    } catch (URIError) {
      // I think this is a problem with right to left language encoding
    }
    boardUrl = `${username}${boardName}/`;
  }

  switch (path) {
    case '/': // homefeed
      return 'UserHomefeedResource';
    case pin.pinner && `/${pin.pinner.username}/pins/`: // profile pins page
      return 'UserPinsResource';
    case /\/visual-search\//.test(path) && path: // visual search/flashlight results
      return 'VisualLiveSearchResultResource';
    case /\/pin\//.test(path) && path: // Closeup related pins feed
      urlBase = '/pin/';
      const pinId = getId(path, urlBase);
      if (isLandingPage) {
        return 'AuthLandingPageResource'; // Pin landing page for auth users
      }
      if (pin.id !== pinId) {
        // If they do match, the pin is the main Closeup one
        return `RelatedPinFeedResource_${pinId}`;
      }
      break;
    case /\/search\//.test(path) && path: // search pages
      if (queryStr) {
        const query = queryStr.split('q=')[1].split('&')[0];
        return `SearchResource_${query}`;
      }
      break;
    case /\/discover\/article\//.test(path) && path: // Explore article feed
      urlBase = '/discover/article/';
      const storyId = getId(path, urlBase);
      return `ExploreTabStoryResource_${storyId}`;
    case /\/categories\//.test(path) && path: // non managed countries Categories feed
      urlBase = '/categories/';
      const category = getId(path, urlBase);
      return `CategoryFeedResource_${category}`;
    case /\/discover\/topics\//.test(path) && path: // related pins for main explore landing page.
      return 'CategoryFeedResource';
    case /\/news_hub\//.test(path) && path: // news hub story page
      urlBase = '/news_hub/';
      const newsHubId = getId(path, urlBase);
      return `NewsHubDetailsResource_${newsHubId}`;
    case /\/topics\//.test(path) && path: // topics pages
      urlBase = '/topics/';
      const topic = getId(path, urlBase);
      return `TopicFeedResource_${topic}`;
    case pin.pinner && `/${pin.pinner.username}/products/`: // profile products page
      return 'StoreFrontFeedResource';
    case /\/source\//.test(path) && path: // domain pages
      urlBase = '/source/';
      const domain = getId(path, urlBase);
      return `DomainFeedResource_${domain}`;
    case /\/explore\//.test(path) && path: // explore KLP pages
      urlBase = '/explore/';
      const klpPage = getId(path, urlBase);
      return `SearchResource_${klpPage}`;
    case boardUrl: // profile boards page
      return 'BoardFeedResource';
    case boardUrl && path.indexOf(boardUrl) > -1 && path: // profile boards section page
      return 'BoardSectionPinsResource';
    case isLandingPage && path: // Board landing pages where the URL does not match the resource
      return 'SearchResource';
    case /\/following\//.test(path) && path: // following feed
      return 'FollowingFeedResource';
    case /\/brand_catalog\//.test(path) && path: // brand catalog feed, aka brand package landing page
      return 'BrandCatalogFeedResource';
    default:
  }

  return '';
};

export type Entry = {
  pathname: string,
  search?: string,
  previousPath?: string,
  gridItems?: Array<Object> | null,
};

type Location = {
  history: Array<Entry>,
  pathname: string,
};

const getPath = (entry: Entry) => {
  const { pathname, search } = entry;
  let path = pathname;

  if (!path) {
    return '';
  }

  // We need this to handle search correctly. After changing the scope of a search, location.pathname will not
  // include the original query
  if (path.match(/\/search\//)) {
    if (search) {
      path = pathname + search;
    }
  }

  return path.toLowerCase();
};

const getClientTrackingParams = (pin: Pin, location: Location) => {
  if (!pin || !pin.tracking_params || !location) {
    return null;
  }

  if (!pin.tracking_params_map) {
    return `${pin.tracking_params}~0`;
  }

  const { history } = location;
  const entry = history && history[history.length - 1];

  let path;
  if (entry) {
    path = getPath(entry);
  } else {
    path = location.pathname;
  }

  const key = getMapKey(path, pin);
  let alternateKey;
  let clientTrackingParams = pin.tracking_params_map[key];

  // If a map exists but doesn't match on the current pathname, try the previous location
  // This happens sometimes when we exit out of a modal that logs pin impressions (i.e. bubbles )
  // This also happens for logging the clientTrackingParams on Clickthroughs for Closeup
  if (!clientTrackingParams && history && history.length > 1) {
    path = getPath(history[history.length - 2]);
    alternateKey = getMapKey(path, pin);
    clientTrackingParams = pin.tracking_params_map[alternateKey];
  }

  // If no tracking params match could be found, we fallback to the clientTrackingParams on the Pin object_type
  // and add ~0 to it to indicate it used this backup (and potentially less correct) mechanism.
  if (!clientTrackingParams) {
    clientTrackingParams = `${pin.tracking_params}~0`;
  }

  return clientTrackingParams;
};

export default getClientTrackingParams;
